import React, {createContext, ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {appRoot, getLogin, getServerCode, setLogin, setNext, setServerCode, setToken} from '../Api/Tools/Api';
import LocalStorage from '../Tools/LocalStorage';
import {APP_PATH, AUTH_PATH, LLM_INTERACTION_CATEGORIES} from '../Tools/Constants';
import {useNavigate} from 'react-router-dom';
import {initDaysJs, setDaysLocale} from "../Tools/DayJs";

export interface UserContextProps {
    login: string;
    botUUID: string;
    lang: string;
    setLogin: (user: string) => void;
    nextUrl: string;
    encodedNextUrl: string;
    currentNavigationStep: number;
    setCurrentNavigationStep?: (step: number) => void;
}

export const UserContext = createContext({} as UserContextProps);

export const useUserContext = () => useContext<UserContextProps>(UserContext);

enum QueryParams {
    id = 'id',
    host = 'host',
    login = 'login',
    botUUID = 'botUUID',
    lang = 'lang',
    proxy = 'proxy',
    next = 'next',
    isOnPrem = 'isOnPrem',
    debugToken = 'debugToken',
}

const UserProvider = ({children}: { children: ReactNode }) => {
    const [user, setUser] = useState<string>('');
    const [botUUID, setBotUUID] = useState<string>('');
    const [lang, setLang] = useState<string>('fr');

    const [tokenHasBeenRequested, setTokenHasBeenRequested] = useState<boolean>(false);

    const [currentNavigationStep, setCurrentNavigationStep] = useState<number>(0);
    const navigate = useNavigate();
    const url = useRef(window.location.href);
    console.log('userProvider run ' + window.location.href);

    initDaysJs();

    /**
     * Init DayJS when language changes
     */
    useEffect(() => {
        if (lang === 'fr') {
            setDaysLocale(lang);
        } else {
            setDaysLocale('en');
        }
    }, [lang]);

    useEffect(() => {
        if (url.current.includes(AUTH_PATH)) {
            url.current = url.current.replace('#', '/');
            const location = new URL(url.current);
            const queryParameters = new URLSearchParams(location.search);

            const id = queryParameters.get(QueryParams.id);
            const host = queryParameters.get(QueryParams.host);
            const botUUID = queryParameters.get(QueryParams.botUUID);
            const lang = queryParameters.get(QueryParams.lang);
            const proxy = queryParameters.get(QueryParams.proxy);
            const next = queryParameters.get(QueryParams.next);
            const isOnPrem = queryParameters.get(QueryParams.isOnPrem);
            const debugToken = queryParameters.get(QueryParams.debugToken);
            const handleMessage = (msg: MessageEvent) => {
                if (msg.data) {
                    if (msg.data.type === 'provide_jwt_and_login') {
                        setToken(msg.data.jwt);
                        setLogin(msg.data.login);
                        setUser(msg.data.login);
                        console.log('Token is set on url ', url.current + ' and href: ' + window.location.href);
                        console.log(`id: ${id} host: ${getServerCode()} login: ${getLogin()} botUUID: ${botUUID}
                            lang: ${lang} proxy: ${proxy} isOnPrem: ${isOnPrem}`);
                        navigate(APP_PATH.AUTH); // will redirect on NEXT path
                    }
                }
            };
            window.addEventListener('message', handleMessage);
            console.log("Event listener messages OK");

            if (!tokenHasBeenRequested) {
                const requestToken = () => {
                    if (window.parent !== window) {
                        window.parent.postMessage({type: 'ask_for_jwt_and_login'}, "*");
                        console.log('Sent ask_for_jwt_and_login to parent');
                        setTokenHasBeenRequested(true);
                    }
                };
                requestToken();
            }

            if (lang) {
                console.log("Language is ", lang);
                LocalStorage.locale.save(lang);
                setLang(lang);
            }

            host && setServerCode(host);
            botUUID && setBotUUID(botUUID);

            if (next && next === LLM_INTERACTION_CATEGORIES) {
                setNext(APP_PATH.LLM_CATEGORISATION_INTERACTIONS);
            }

            if (debugToken) {
                setToken(debugToken);
                console.log('Token is set on url ', url.current + ' and href: ' + window.location.href);
                navigate(APP_PATH.AUTH); // will redirect on NEXT path
            }
        }
    }, [navigate, tokenHasBeenRequested]);

    const cleanAppRoot = appRoot !== '/' ? appRoot : '';
    const nextUrl = new URL(window.location.href).origin + cleanAppRoot + AUTH_PATH;

    const encodedNextUrl = encodeURIComponent(nextUrl);

    const props: UserContextProps = {
        login: user,
        botUUID,
        lang,
        setLogin: setUser,
        nextUrl: nextUrl,
        encodedNextUrl,
        currentNavigationStep: currentNavigationStep,
        setCurrentNavigationStep: setCurrentNavigationStep,
    };

    return <UserContext.Provider children={children} value={props}/>;
};

export default UserProvider;
